import React, { useCallback, useMemo, useState } from 'react';

type BaseImgProps = Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  'src' | 'onError'
>;

export type ImageWithFallbackProps = BaseImgProps & {
  /**
   * Image URL.
   *
   * Note: can be empty/undefined; in that case fallback will be used.
   */
  src?: string;
  /** Alt text for accessibility */
  alt: string;
  /**
   * Fallback image URL.
   * If omitted and `src` fails, component renders nothing for the image.
   */
  fallbackSrc?: string;
  /**
   * Test id for unit/e2e selectors.
   */
  'data-testid'?: string;
};

const ImageWithFallback = ({
  src,
  alt,
  fallbackSrc,
  onLoad,
  className,
  loading,
  decoding,
  referrerPolicy,
  crossOrigin,
  width,
  height,
  sizes,
  style,
  ...rest
}: ImageWithFallbackProps) => {
  const [resolvedSrc, setResolvedSrc] = useState<string | undefined>(src);
  const [didFallbackError, setDidFallbackError] = useState(false);

  const effectiveFallbackSrc = useMemo(() => fallbackSrc, [fallbackSrc]);

  const handleError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      // If consumer passed an onError, keep it working.
      // (We intentionally allow it even though it's omitted from the prop type.)
      const maybeOnError = (rest as any).onError as
        | ((ev: React.SyntheticEvent<HTMLImageElement, Event>) => void)
        | undefined;
      if (maybeOnError) maybeOnError(e);

      // If no fallback is provided, stop trying.
      if (!effectiveFallbackSrc) return;

      // Prevent infinite loops: if we've already errored while using fallback, stop.
      if (didFallbackError) return;

      // Avoid re-setting if already on fallback.
      if (resolvedSrc === effectiveFallbackSrc) {
        setDidFallbackError(true);
        return;
      }

      setResolvedSrc(effectiveFallbackSrc);
    },
    [didFallbackError, effectiveFallbackSrc, resolvedSrc, rest]
  );

  return (
    <img
      {...rest}
      data-testid={rest['data-testid']}
      src={resolvedSrc}
      alt={alt}
      className={className}
      loading={loading}
      decoding={decoding}
      referrerPolicy={referrerPolicy}
      crossOrigin={crossOrigin}
      width={width}
      height={height}
      sizes={sizes}
      style={style}
      onLoad={onLoad}
      onError={handleError}
    />
  );
};

export default ImageWithFallback;

